import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { flagFor } from "../lib/countryFlags";

export default function CountrySearchSelect({
  countries, value, onChange, placeholder = "Select country…", inputClass, testid = "country-search", extraOptions = [],
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  const extras = extraOptions.map((o) => ({ ...o, isExtra: true }));
  const all = [...countries.map((c) => ({ value: c, label: c })), ...extras];
  const selected = all.find((o) => o.value === value);

  const list = (q ? all.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : all).slice(0, 50);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (o) => {
    onChange(o.value);
    setOpen(false);
    setQ("");
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
              aria-expanded={open} aria-haspopup="listbox"
              className={`${inputClass} flex cursor-pointer items-center justify-between gap-2 text-left`}>
        <span className={`truncate ${selected ? "" : "text-slate-400"}`}>
          {selected ? `${selected.isExtra ? "🌐" : flagFor(selected.value)} ${selected.label}` : placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-2xl" role="listbox">
          <div className="flex items-center gap-2 border-b border-stone-100 px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-input`}
                   onKeyDown={(e) => {
                     if (e.key === "Escape") setOpen(false);
                     if (e.key === "Enter" && list.length) { e.preventDefault(); pick(list[0]); }
                   }}
                   placeholder="Type to search — e.g. uni → United Kingdom"
                   className="w-full bg-transparent py-1.5 text-sm text-navy outline-none placeholder:text-slate-400" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {list.map((o, i) => (
              <button type="button" key={o.value} role="option" aria-selected={o.value === value}
                      onClick={() => pick(o)} data-testid={`${testid}-option-${i}`}
                      className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium transition-colors hover:bg-saffron-light hover:text-saffron ${o.value === value ? "bg-saffron-light/60 text-saffron" : "text-navy"}`}>
                <span className="text-base leading-none">{o.isExtra ? "🌐" : flagFor(o.value)}</span>
                {o.label}
              </button>
            ))}
            {!list.length && <p className="px-4 py-5 text-center text-xs font-semibold text-slate-400" data-testid={`${testid}-empty`}>No country matches “{q}”</p>}
          </div>
        </div>
      )}
    </div>
  );
}
